import React, { useEffect, useRef, useState } from 'react'
import {
   Button,
   Field,
   Input,
   Menu,
   MenuItem,
   MenuList,
   MenuPopover,
   MenuTrigger,
   Popover,
   PopoverSurface,
   PositioningImperativeRef,
   Toolbar,
   ToolbarButton,
   ToolbarGroup,
   makeStyles,
   tokens,
} from '@fluentui/react-components'
import { useCurrentEditor } from '@tiptap/react'
import {
   ChevronDown12Regular,
   Link16Filled,
   Link16Regular,
   TextBold16Filled,
   TextBold16Regular,
   TextBulletList16Filled,
   TextBulletList16Regular,
   TextIndentDecrease16Regular,
   TextIndentIncrease16Regular,
   TextItalic16Filled,
   TextItalic16Regular,
   TextNumberListLtr16Filled,
   TextNumberListLtr16Regular,
   TextUnderline16Filled,
   TextUnderline16Regular,
} from '@fluentui/react-icons'
import { t } from '../../../i18n/i18n'
import { ILanguage } from '../../../interfaces/ILanguage'
import { canDecreaseIndent, canIncreaseIndent, decreaseIndent, increaseIndent } from './extensions/Indent'
import {
   ExplicitOrderedListMarker,
   applyOrderedList,
   getEffectiveOrderedListMarker,
} from './extensions/OrderedListType'

/**
 * Menu order, i18n key and the glyph shown where Fluent has no icon (there is no lettered-list
 * glyph in the icon set). Adding a marker the CSS already understands is one row here plus one
 * translation key.
 *
 * Note that "Numbered list" sends `'1'` rather than `null`. Since #6476 an `<ol>` with no `type`
 * follows the positional cascade, so `null` on a level 3 list would render `i. ii. iii.` — the
 * author asking for numbers has to say so explicitly.
 */
const ORDERED_LIST_OPTIONS: {
   marker: ExplicitOrderedListMarker
   labelKey: keyof ILanguage
   preview: string
}[] = [
   { marker: '1', labelKey: 'NumberedList', preview: '1. 2. 3.' },
   { marker: 'a', labelKey: 'LetteredList', preview: 'a. b. c.' },
   { marker: 'A', labelKey: 'UpperLetteredList', preview: 'A. B. C.' },
]

const useStyles = makeStyles({
   menubar: {
      borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
      gap: tokens.spacingHorizontalXL,
   },
   popoverSurface: {
      display: 'flex',
      gap: tokens.spacingHorizontalXL,
   },
   active: {
      backgroundColor: tokens.colorNeutralBackground1Selected,
   },
   group: {
      display: 'flex',
      gap: tokens.spacingHorizontalXXS,
   },
   // Preview of the marker style, shown because Fluent has no lettered-list glyph in the
   // icon set. It sits in the item's content rather than its `icon` slot: that slot is
   // sized for a 20px icon and clips anything wider, which cut "1. 2. 3." down to "2. 3.".
   // The fixed width keeps the labels lined up in a column.
   markerPreview: {
      display: 'inline-block',
      width: '68px',
      minWidth: '68px',
      flexShrink: 0,
      fontSize: tokens.fontSizeBase200,
      color: tokens.colorNeutralForeground2,
   },
})
export const MenuBar = () => {
   const { editor } = useCurrentEditor()
   const [linkPopoverOpen, setLinkPopoverOpen] = useState<boolean>(false)
   const [url, setUrl] = useState<string>('')

   const classes = useStyles()

   const buttonRef = useRef<HTMLButtonElement>(null)
   const positioningRef = useRef<PositioningImperativeRef>(null)
   const handleInsertLink = () => {
      if (url) {
         editor.chain().focus().setLink({ href: url }).run()
         setUrl('')
         setLinkPopoverOpen(false)
      }
   }
   useEffect(() => {
      if (buttonRef.current) {
         positioningRef.current?.setTarget(buttonRef.current)
      }
   }, [buttonRef, positioningRef])
   if (!editor) {
      return null
   }

   const isOrderedList = editor.isActive('orderedList')
   const activeMarker = isOrderedList ? getEffectiveOrderedListMarker(editor) : null

   const handleOrderedList = (marker: ExplicitOrderedListMarker) => {
      applyOrderedList(editor, marker)
      // Fluent's MenuPopover restores focus to its trigger button when it closes,
      // which happens after this handler. Put the caret back in the editor afterwards.
      window.setTimeout(() => {
         if (!editor.isDestroyed) {
            editor.commands.focus()
         }
      }, 0)
   }

   return (
      <Toolbar className={classes.menubar}>
         <ToolbarGroup className={classes.group}>
            <ToolbarButton
               className={editor.isActive('bold') ? classes.active : ''}
               icon={editor.isActive('bold') ? <TextBold16Filled /> : <TextBold16Regular />}
               aria-label="bold"
               onClick={() => editor.chain().focus().toggleBold().run()}
               disabled={!editor.can().chain().focus().toggleBold().run()}
            />
            <ToolbarButton
               className={editor.isActive('underline') ? classes.active : ''}
               icon={editor.isActive('underline') ? <TextUnderline16Filled /> : <TextUnderline16Regular />}
               aria-label="underline"
               onClick={() => editor.chain().focus().toggleUnderline().run()}
               disabled={!editor.can().chain().focus().toggleUnderline().run()}
            />
            <ToolbarButton
               className={editor.isActive('italic') ? classes.active : ''}
               icon={editor.isActive('italic') ? <TextItalic16Filled /> : <TextItalic16Regular />}
               aria-label="italic"
               onClick={() => editor.chain().focus().toggleItalic().run()}
               disabled={!editor.can().chain().focus().toggleItalic().run()}
            />
            <ToolbarButton
               className={editor.isActive('link') ? classes.active : ''}
               icon={editor.isActive('link') ? <Link16Filled /> : <Link16Regular />}
               aria-label="link"
               ref={buttonRef}
               onClick={() => {
                  const { from, to } = editor.state.selection
                  const selectedText = editor.state.doc.textBetween(from, to, '')
                  if (to - from !== 0 && selectedText) {
                     setUrl(selectedText)
                     setLinkPopoverOpen(true)
                  }
               }}
            />
            <Popover open={linkPopoverOpen} onOpenChange={(e, data) => setLinkPopoverOpen(data.open)} positioning={{ positioningRef }}>
               <PopoverSurface className={classes.popoverSurface}>
                  <Field label={t('EnterURL') + ':'} orientation="horizontal">
                     <Input type="text" value={url} onChange={(e) => setUrl(e.target.value)} />
                  </Field>
                  <Button appearance="subtle" onClick={handleInsertLink}>
                     {t('Save')}
                  </Button>
               </PopoverSurface>
            </Popover>
         </ToolbarGroup>
         <ToolbarGroup className={classes.group}>
            <ToolbarButton
               className={editor.isActive('bulletList') ? classes.active : ''}
               icon={editor.isActive('bulletList') ? <TextBulletList16Filled /> : <TextBulletList16Regular />}
               aria-label={t('BulletList')}
               onClick={() => editor.chain().focus().toggleBulletList().run()}
            />
            <Menu>
               <MenuTrigger disableButtonEnhancement>
                  <ToolbarButton
                     className={isOrderedList ? classes.active : ''}
                     icon={isOrderedList ? <TextNumberListLtr16Filled /> : <TextNumberListLtr16Regular />}
                     aria-label={t('NumberedList')}
                  >
                     <ChevronDown12Regular />
                  </ToolbarButton>
               </MenuTrigger>
               <MenuPopover>
                  <MenuList>
                     {ORDERED_LIST_OPTIONS.map(({ marker, labelKey, preview }) => (
                        <MenuItem
                           key={marker}
                           className={activeMarker === marker ? classes.active : ''}
                           onClick={() => handleOrderedList(marker)}
                        >
                           <span className={classes.markerPreview}>{preview}</span>
                           {t(labelKey)}
                        </MenuItem>
                     ))}
                  </MenuList>
               </MenuPopover>
            </Menu>
         </ToolbarGroup>
         <ToolbarGroup className={classes.group}>
            <ToolbarButton
               icon={<TextIndentIncrease16Regular />}
               aria-label={t('IncreaseIndent')}
               onClick={() => increaseIndent(editor)}
               disabled={!canIncreaseIndent(editor)}
            />
            <ToolbarButton
               icon={<TextIndentDecrease16Regular />}
               aria-label={t('DecreaseIndent')}
               onClick={() => decreaseIndent(editor)}
               disabled={!canDecreaseIndent(editor)}
            />
         </ToolbarGroup>
      </Toolbar>
   )
}
